<script lang="ts">
    import { getContext, onDestroy } from "svelte";
    import type { Writable } from "svelte/store";
    import { Routes, CellState, type Attempt, type Puzzle } from "@lib/types";
    import { PuzzleDate } from "@lib/util";
    import { db } from "@lib/db";
    import { play } from "@lib/api";
    import { logEvent } from "@lib/events";
    import PuzzleComponent from "@components/puzzle/Puzzle.svelte";
    import BackButton from "@components/common/BackButton.svelte";
    import Settings from "@components/common/Settings.svelte";
    import { fade } from "svelte/transition";

    let router = getContext<Writable<Routes>>("router");
    let date = getContext<Writable<PuzzleDate>>("date");

    let puzzle: Puzzle | undefined = $state(undefined);
    let attempt: Attempt | undefined = $state(undefined);
    let loadError: string | null = $state(null);

    onDestroy(
        date.subscribe(async (newDate: PuzzleDate) => {
            loadError = null;

            try {
                puzzle = await db.puzzles.get({ date: newDate.iso });
            } catch (e) {
                // noop
            }

            if (!puzzle) {
                try {
                    const { session, ...newPuzzle } = await play(newDate.iso);
                    if (newPuzzle) {
                        const newAttempt: Attempt = {
                            puzzle_id: newPuzzle.id,
                            session: session,
                            timer: { elapsed: 0, paused: false },
                            placements: Array(newPuzzle.size ** 2).fill(
                                CellState.Empty,
                            ) as Array<CellState>,
                            snapshots: [] as Array<Array<CellState>>,
                            hint_cooldown_until: 0,
                            solved: false,
                        };

                        await db.transaction(
                            "rw",
                            db.puzzles,
                            db.attempts,
                            async () => {
                                await db.puzzles.put(newPuzzle);
                                const attemptId = await db.attempts.put(newAttempt);
                                newAttempt.id = attemptId;
                            },
                        );

                        puzzle = newPuzzle;
                        attempt = newAttempt;

                        void logEvent({
                            session: newAttempt.session,
                            event_type: "attempt_started",
                            elapsed_ms: 0,
                            data: {
                                puzzle_id: newPuzzle.id,
                                date: newPuzzle.date,
                                size: newPuzzle.size,
                                fit: newPuzzle.fit,
                            },
                        });
                    }
                } catch (e) {
                    // noop
                    const message =
                        e instanceof Error && e.message === "Puzzle not found"
                            ? "Today's puzzle isn't ready yet. Please check back later."
                            : e instanceof Error && e.message
                                ? e.message
                                : "Failed to start today's puzzle.";
                    loadError = message;
                }
            }

            if (puzzle && !attempt) {
                try {
                    attempt = await db.attempts.get({ puzzle_id: puzzle.id });
                } catch (e) {
                    // noop
                    goToMenu();
                }
            }
        }),
    );

    const goToMenu = () => {
        router.set(Routes.Menu);
        window.history.back();
    };
</script>

<div class="container">
    <main>
        {#if puzzle && attempt}
            <div transition:fade={{ duration: 150 }}>
                <header class="puzzle-header">
                    <div>
                        <BackButton onclick={goToMenu} />
                    </div>
                    <div class="title">
                        <h3>TWO KINGS</h3>
                        <h5>#{puzzle.id}</h5>
                    </div>
                    <div class="options">
                        <Settings />
                    </div>
                </header>
                <PuzzleComponent {puzzle} {attempt} />
            </div>
        {:else if loadError}
            <div class="puzzle-error">
                <p>{loadError}</p>
                <button class="button" type="button" onclick={goToMenu}>
                    Back to menu
                </button>
            </div>
        {/if}
    </main>
</div>

<style>
    .puzzle-error {
        margin: auto 1.5rem;
        padding: 2rem 1.5rem;
        border: 2px solid var(--black);
        border-radius: 8px;
        background-color: var(--accent-color);
        color: var(--black);
        text-align: center;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        align-items: center;
        justify-content: center;
        animation: 0.25s fade ease;
    }

    .puzzle-error p {
        max-width: 24rem;
    }

    .puzzle-error .button {
        width: 100%;
    }
</style>
